/**
 * Checkout totals from orders service fee API (delivery includes service; processing is online-only).
 */

function num(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * @param {object} params
 * @param {number} params.itemsSubtotal
 * @param {number} params.walletBalance
 * @param {boolean} params.useWalletForPoints
 * @param {object|null} params.feeConfigOnline - GET fees with fee_payment_method=card
 * @param {object|null} params.feeConfigInstapay - GET fees with fee_payment_method=instapay
 * @param {string} params.feePaymentMethod
 */
export function deriveCheckoutPricing({
  itemsSubtotal,
  walletBalance,
  useWalletForPoints,
  feeConfigOnline,
  feeConfigInstapay,
  feePaymentMethod,
}) {
  const items = num(itemsSubtotal);
  const balance = num(walletBalance);
  const baseConfig = feeConfigOnline || feeConfigInstapay;
  const isInstapay = feePaymentMethod === 'instapay';

  const deliveryFee = num(
    feeConfigOnline?.delivery_fee_egp ?? feeConfigInstapay?.delivery_fee_egp
  );
  // Processing applies only to online methods — Instapay config should be 0.
  const onlineProcessingFee = num(feeConfigOnline?.payment_processing_fee_egp);
  const instapayProcessingFee = num(feeConfigInstapay?.payment_processing_fee_egp);
  const processingFee = isInstapay ? instapayProcessingFee : onlineProcessingFee;
  const extraItemsFee = num(
    feeConfigOnline?.extra_items_fee_egp ?? feeConfigInstapay?.extra_items_fee_egp
  );

  const onlineFeesTotal = num(feeConfigOnline?.total_fees_egp);
  const instapayFeesTotal = num(
    feeConfigInstapay?.total_fees_egp,
    num(feeConfigInstapay?.delivery_fee_egp ?? feeConfigOnline?.delivery_fee_egp)
  );
  const instapayDiscount = 0;

  const itemsFromWallet = useWalletForPoints ? Math.min(balance, items) : 0;
  const itemsOutOfPocket = Math.max(0, items - itemsFromWallet);

  const walletAppliedToFeesOnline = num(feeConfigOnline?.wallet_applied_to_fees_egp);
  const walletAppliedToFeesInstapay = num(
    feeConfigInstapay?.wallet_applied_to_fees_egp
  );
  const paymobFeesFromApi = num(
    feeConfigOnline?.paymob_fees_egp,
    onlineFeesTotal || deliveryFee
  );

  const onlineFinalDue = itemsOutOfPocket + paymobFeesFromApi;
  const instapayFinalDue =
    itemsOutOfPocket + Math.max(0, instapayFeesTotal - walletAppliedToFeesInstapay);

  const selectedPaymentAmount = isInstapay ? instapayFinalDue : onlineFinalDue;

  const walletAppliedTotalInstapay = itemsFromWallet + walletAppliedToFeesInstapay;
  const totalBeforeWalletInstapay = items + instapayFeesTotal;

  return {
    deliveryFee,
    processingFee,
    onlineProcessingFee,
    extraItemsFee,
    onlineFeesTotal: onlineFeesTotal || deliveryFee,
    instapayFeesTotal,
    instapayDiscount,
    feesBeforeInstapayDiscount: instapayFeesTotal,
    itemsFromWallet,
    itemsOutOfPocket,
    walletAppliedToFeesOnline,
    walletAppliedToFeesInstapay,
    onlineFinalDue,
    instapayFinalDue,
    selectedPaymentAmount,
    walletAppliedTotalInstapay,
    totalBeforeWalletInstapay,
    hasFeeConfig: Boolean(baseConfig),
  };
}
